import React, { useState, useEffect } from 'react';
import { FileText, Package, Crown, Loader2, Scale, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { BookMockup, getBadge } from '../components/BookMockup';
import { ExitIntentPopup } from '../components/ExitIntentPopup';
import { HorizontalScroll, ScrollCard } from '../components/HorizontalScroll';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import axios from 'axios';
import { createCheckoutSession } from '../services/paymentService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_ORDER = [
  'Credit Disputes & Credit Reporting',
  'Business Credit & Business Setup',
  'Court Motions & Legal Filings',
  'Affidavits & Sworn Statements',
  'Trust & Estate Planning',
  'Legal Notices & Demand Letters',
  'Landlord-Tenant Disputes',
  'Lawsuits & Civil Complaints',
];

const SHORT_NAMES = {
  'Credit Disputes & Credit Reporting': 'Credit Disputes',
  'Business Credit & Business Setup': 'Business Credit',
  'Court Motions & Legal Filings': 'Court Motions',
  'Affidavits & Sworn Statements': 'Affidavits',
  'Trust & Estate Planning': 'Trust & Estate',
  'Legal Notices & Demand Letters': 'Legal Notices',
  'Landlord-Tenant Disputes': 'Landlord-Tenant',
  'Lawsuits & Civil Complaints': 'Civil Lawsuit Templates & Filing Guide',
  'All Categories': 'All Categories',
};

const PRICING_INFO = {
  bundle_5: { was: 60, now: 39, save: 20 },
  bundle_10: { was: 120, now: 89, save: 30 },
  bundle_category: { was: 240, now: 150, save: 89 },
  bundle_lawsuit: { was: 175, now: 150, save: 25 },
  vault: { was: 1764, now: 1000, save: 764 },
};

const StrikethroughPrice = ({ type }) => {
  const info = PRICING_INFO[type];
  if (!info) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-gray-400 line-through text-sm font-medium">${info.was}</span>
      <span className="text-green-600 text-xs font-bold bg-green-50 px-2 py-0.5 rounded-full">
        Save ${info.save}
      </span>
    </div>
  );
};

const BundlesPage = () => {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    const fetchBundles = async () => {
      try {
        const resp = await axios.get(`${API}/bundles`);
        setBundles(resp.data.bundles);
      } catch (err) {
        console.error('Error fetching bundles:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBundles();
  }, []);

  const handleQuickBuy = async (productId) => {
    setPurchasing(productId);
    try {
      const session = await createCheckoutSession(productId);
      if (session.url) window.location.href = session.url;
    } catch { alert('Unable to process. Please try again.'); }
    finally { setPurchasing(null); }
  };

  // Sort: lowest to highest price
  const bundle5s = bundles.filter(b => b.product_type === 'bundle_5').sort((a, b) => a.price - b.price);
  const bundle10s = bundles.filter(b => b.product_type === 'bundle_10').sort((a, b) => a.price - b.price);
  const bundleCats = bundles.filter(b => b.product_type === 'bundle_category').sort((a, b) => a.price - b.price);
  const lawsuitBundle = bundles.find(b => b.product_type === 'bundle_lawsuit');
  const vault = bundles.find(b => b.product_type === 'vault');

  const groupByCategory = (items) => {
    const groups = {};
    for (const item of items) {
      const cat = item.category;
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    }
    return CATEGORY_ORDER
      .filter(cat => groups[cat])
      .map(cat => ({ category: cat, displayName: SHORT_NAMES[cat] || cat, bundles: groups[cat] }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-r from-red-700 via-orange-600 to-orange-500 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3" data-testid="bundles-page-title">Bundles & Deals</h1>
          <p className="text-base md:text-lg text-white/90">Save big with our curated bundles — up to 50% off individual prices</p>
        </div>
      </section>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin" />
        </div>
      )}

      {!loading && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-14" data-testid="bundles-page-content">

          {/* 5-Guide Bundles — $39 (lowest price first) */}
          {bundle5s.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-1" data-testid="five-bundles-heading">
                5-Guide Bundles
              </h2>
              <p className="text-sm text-gray-500 mb-1">Best value — pick a topic and get started</p>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-gray-400 line-through text-sm">$60</span>
                <span className="text-xl font-bold text-gray-900">$39</span>
                <span className="bg-green-50 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">Save $20</span>
              </div>
              {groupByCategory(bundle5s).map(group => (
                <div key={group.category} className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-700 mb-3">{group.displayName}</h3>
                  <HorizontalScroll>
                    {group.bundles.map(bundle => (
                      <ScrollCard key={bundle.id} width="w-52 sm:w-60">
                        <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-200 hover:border-orange-300 h-full" data-testid={`bundle-5-page-${bundle.id}`}>
                          <a href={`/bundle/${bundle.id}`} className="block">
                            <BookMockup productId={bundle.id} title={bundle.title} size="card" badge={getBadge(bundle)} />
                          </a>
                          <div className="p-3">
                            <h3 className="text-xs font-bold text-gray-900 mb-1 line-clamp-2 text-center">{bundle.title}</h3>
                            <div className="flex items-center justify-between mt-2">
                              <a href={`/bundle/${bundle.id}`} className="text-xs text-orange-600 hover:text-orange-800 font-medium transition-colors" data-testid={`view-bundle-5-${bundle.id}`}>
                                View Details
                              </a>
                              <button
                                className="bg-orange-500 hover:bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors shadow-sm"
                                onClick={(e) => { e.preventDefault(); window.location.href = `/bundle/${bundle.id}`; }}
                                disabled={purchasing === bundle.id}
                                data-testid={`buy-bundle-5-${bundle.id}`}
                              >
                                {purchasing === bundle.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight size={14} />}
                              </button>
                            </div>
                          </div>
                        </div>
                      </ScrollCard>
                    ))}
                  </HorizontalScroll>
                </div>
              ))}
            </section>
          )}

          {/* Advanced Bundles — $89 */}
          {bundle10s.length > 0 && (
            <section id="advanced-bundles">
              <h2 className="text-2xl font-bold text-gray-900 mb-1" data-testid="ten-bundles-heading">
                Advanced Bundles (10 Guides)
              </h2>
              <p className="text-sm text-gray-500 mb-1">Go deeper — comprehensive coverage per topic</p>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-gray-400 line-through text-sm">$120</span>
                <span className="text-xl font-bold text-gray-900">$89</span>
                <span className="bg-green-50 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">Save $30</span>
              </div>
              {groupByCategory(bundle10s).map(group => (
                <div key={group.category} className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-700 mb-3">{group.displayName}</h3>
                  <HorizontalScroll>
                    {group.bundles.map(bundle => (
                      <ScrollCard key={bundle.id} width="w-52 sm:w-60">
                        <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-200 hover:border-orange-300 h-full" data-testid={`bundle-10-page-${bundle.id}`}>
                          <a href={`/bundle/${bundle.id}`} className="block">
                            <BookMockup productId={bundle.id} title={bundle.title} size="card" badge={getBadge(bundle)} />
                          </a>
                          <div className="p-3">
                            <h3 className="text-xs font-bold text-gray-900 mb-1 line-clamp-2 text-center">{bundle.title}</h3>
                            <div className="flex items-center justify-between mt-2">
                              <a href={`/bundle/${bundle.id}`} className="text-xs text-orange-600 hover:text-orange-800 font-medium transition-colors" data-testid={`view-bundle-10-${bundle.id}`}>
                                View Details
                              </a>
                              <button
                                className="bg-orange-500 hover:bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors shadow-sm"
                                onClick={(e) => { e.preventDefault(); window.location.href = `/bundle/${bundle.id}`; }}
                                disabled={purchasing === bundle.id}
                                data-testid={`buy-bundle-10-${bundle.id}`}
                              >
                                {purchasing === bundle.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight size={14} />}
                              </button>
                            </div>
                          </div>
                        </div>
                      </ScrollCard>
                    ))}
                  </HorizontalScroll>
                </div>
              ))}
            </section>
          )}

          {/* Category Bundles — $149 */}
          {bundleCats.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-1" data-testid="category-bundles-heading">
                Full Systems
              </h2>
              <p className="text-sm text-gray-500 mb-1">Complete category coverage — every guide in one package</p>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-gray-400 line-through text-sm">$240</span>
                <span className="text-xl font-bold text-gray-900">$150</span>
                <span className="bg-green-50 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">Save $89</span>
              </div>
              {groupByCategory(bundleCats).map(group => (
                <div key={group.category} className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-700 mb-3">{group.displayName}</h3>
                  <HorizontalScroll>
                    {group.bundles.map(bundle => (
                      <ScrollCard key={bundle.id} width="w-52 sm:w-60">
                        <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-200 hover:border-orange-300 h-full" data-testid={`bundle-cat-${bundle.id}`}>
                          <a href={`/bundle/${bundle.id}`} className="block">
                            <BookMockup productId={bundle.id} title={bundle.title} size="card" badge={getBadge(bundle)} />
                          </a>
                          <div className="p-3">
                            <h3 className="text-xs font-bold text-gray-900 mb-1 line-clamp-2 text-center">{bundle.title}</h3>
                            <div className="flex items-center justify-between mt-2">
                              <a href={`/bundle/${bundle.id}`} className="text-xs text-orange-600 hover:text-orange-800 font-medium transition-colors" data-testid={`view-bundle-cat-${bundle.id}`}>
                                View Details
                              </a>
                              <button
                                className="bg-orange-500 hover:bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors shadow-sm"
                                onClick={(e) => { e.preventDefault(); window.location.href = `/bundle/${bundle.id}`; }}
                                disabled={purchasing === bundle.id}
                                data-testid={`buy-bundle-cat-${bundle.id}`}
                              >
                                {purchasing === bundle.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight size={14} />}
                              </button>
                            </div>
                          </div>
                        </div>
                      </ScrollCard>
                    ))}
                  </HorizontalScroll>
                </div>
              ))}
            </section>
          )}

          {/* Lawsuit Starter Pack — $150 */}
          {lawsuitBundle && (
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2" data-testid="lawsuit-bundle-heading">
                <Scale size={24} className="text-emerald-600" /> Lawsuit Starter Pack
                <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">NEW</span>
              </h2>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-gray-400 line-through text-lg">$175</span>
                <span className="text-2xl font-bold text-gray-900">$150</span>
                <span className="bg-green-100 text-green-700 text-sm font-bold px-3 py-1 rounded-full">Save $25</span>
              </div>
              <a href={`/bundle/${lawsuitBundle.id}`} className="bg-white rounded-xl shadow-xl border-2 border-emerald-300 overflow-hidden block hover:shadow-2xl transition-all" data-testid={`bundle-lawsuit-${lawsuitBundle.id}`}>
                <div className="flex flex-col md:flex-row overflow-hidden">
                  <div className="md:w-56 shrink-0 flex items-center justify-center bg-white py-4">
                    <BookMockup productId={lawsuitBundle.id} title={lawsuitBundle.title} size="lg" badge={getBadge(lawsuitBundle)} />
                  </div>
                  <div className="bg-gradient-to-r from-emerald-600 to-teal-500 p-5 text-white flex-1">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 h-full">
                      <div>
                        <h3 className="text-xl font-bold">{lawsuitBundle.title}</h3>
                        <p className="text-sm text-white/80 mt-1">{lawsuitBundle.guide_count} real-world lawsuit templates with statutory citations & case law exhibits</p>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <div className="text-right">
                          <div className="text-white/60 line-through text-sm">$175</div>
                          <span className="text-3xl font-bold">$150</span>
                        </div>
                        <Button
                          className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 shadow-lg flex items-center gap-2"
                          onClick={(e) => { e.preventDefault(); window.location.href = `/bundle/${lawsuitBundle.id}`; }}
                          disabled={purchasing === lawsuitBundle.id}
                          data-testid="lawsuit-bundle-buy-btn"
                        >
                          {purchasing === lawsuitBundle.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Get Lawsuit Pack <ArrowRight size={16} /></>}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-sm text-gray-600">{lawsuitBundle.description}</p>
                </div>
              </a>
            </section>
          )}

          {/* Vault — $1,000 */}
          {vault && (
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2" data-testid="vault-heading">
                <Crown size={24} className="text-yellow-500" /> All Access Vault
              </h2>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-gray-400 line-through text-lg">$1,764</span>
                <span className="text-2xl font-bold text-gray-900">${vault.price}</span>
                <span className="bg-green-100 text-green-700 text-sm font-bold px-3 py-1 rounded-full">Save $764</span>
              </div>
              <a href={`/bundle/${vault.id}`} className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-xl shadow-2xl overflow-hidden block hover:from-gray-800 hover:to-gray-700 transition-all" data-testid="vault-banner-page">
                <div className="flex flex-col md:flex-row items-center gap-0">
                  <div className="md:w-64 shrink-0 flex items-center justify-center bg-white rounded-l-xl py-4">
                    <BookMockup productId={vault.id} title={vault.title} size="lg" badge={getBadge(vault)} />
                  </div>
                  <div className="p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-4 flex-1">
                    <div>
                      <h3 className="text-xl md:text-2xl font-bold">{vault.title}</h3>
                      <p className="text-sm text-gray-300 mt-1">{vault.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-gray-500 line-through text-sm">$1,764</span>
                        <span className="bg-yellow-500/20 text-yellow-400 text-xs font-bold px-2 py-0.5 rounded-full">Save $764</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <span className="text-4xl font-bold text-yellow-400">${vault.price}</span>
                      <Button
                        className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 flex items-center gap-2"
                        onClick={(e) => { e.preventDefault(); window.location.href = `/bundle/${vault.id}`; }}
                        disabled={purchasing === vault.id}
                        data-testid="vault-page-buy-btn"
                      >
                        {purchasing === vault.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Get Full Access <ArrowRight size={16} /></>}
                      </Button>
                    </div>
                  </div>
                </div>
              </a>
            </section>
          )}

        </div>
      )}

      <Footer />
      <ExitIntentPopup />
    </div>
  );
};

export default BundlesPage;
